/**
 * メッセージ描画レイヤー。
 *
 * ストアは生の `ChatMessageDto[]` (= サーバの source of truth) を保持し、
 * 描画前にこのモジュールで「描画ブロック」の配列へ射影する。1 つの
 * `ChatMessageDto` は content block の構成に応じて複数の描画ブロックを生む
 * (例: text ブロック → テキストバブル、tool_use ブロック → ツールカード)。
 *
 * tool_use と tool_result の対応付け (ツールカードの状態・出力の決定) はここに
 * 集約する。コンポーネント側は描画ブロックを 1:1 で要素へマップするだけにする。
 */

import {
	type ChatMessageDto,
	type ChatSubagentRun,
	type ChatWorkflowRun,
	type SubagentRunStatus,
	ChatMessageRole,
	type FileDiff,
	type DocumentContentBlock,
	type ImageContentBlock,
	hasMeasurableElapsed,
	isHitlRejectionTurn,
	isDocumentContent,
	isUploadedFileContent,
	isImageContent,
	isTerminalSubagentStatus,
	subagentStatusText,
	isToolResultContent,
	isToolUseContent,
	type NoticeKind,
	parseNoticeKind,
	isProviderOutageNotice,
	isProviderAccessNoticeKind,
	STOPPED_TOOL_RESULT_PREFIX,
	ToolCallStatus,
} from "@/lib/chat";
import { formatElapsed } from "@/lib/format-datetime";
import {
	extractContentText,
	parseMessageControls,
	type MessageControl,
} from "@/lib/message-controls";
import { fromUploadedFileBlock, type UploadedFile } from "@/lib/uploaded-file-block";

/** `deriveStreamingView` が必要とする生成中状態の最小形。 */
export interface StreamingViewInput {
	text: string;
	phase: "thinking" | "advising" | "responding" | "preparingTool";
}

/**
 * 生成中バブルと進行中インジケータの出し分けを決める。
 * 生成済みの本文は phase に関わらず出したままにする: advisor 相談や thinking へ切り替わった瞬間に
 * バブルごと消すと、書き終えた段落が advisor の推論中 (十数秒) 画面から消えてレイアウトが飛ぶ
 * (本文はストアに残っており次の text 断片で戻るが、その間ユーザーには失われたように見える)。
 * インジケータは応答生成中以外なら常に出し、本文があればその下へ添える。
 */
export function deriveStreamingView(
	streamingAssistant: StreamingViewInput | null,
	status: string,
	lastIsActiveTool: boolean,
): { streamingText: string; showThinking: boolean } {
	const streamingText = streamingAssistant?.text ?? "";
	return {
		streamingText,
		showThinking:
			status === "streaming" &&
			(!lastIsActiveTool || streamingAssistant?.phase === "preparingTool") &&
			(streamingAssistant?.phase !== "responding" || streamingText.length === 0),
	};
}

/** ユーザー送信メッセージの楽観的状態を描画ブロックへ伝えるための入力。 */
export interface ProjectionContext {
	/** 送信中 (POST 未解決) のユーザーメッセージ id。なければ null。 */
	pendingUserMessageId: string | null;
	/** HITL 入力待ちの tool_use id。なければ null。 */
	waitingToolUseId: string | null;
	/** run id で引けるサブエージェント実行の最新状態 (foreground / background 共通)。 */
	subagentRuns?: ReadonlyMap<string, ChatSubagentRun>;
	/** 親 tool_use id → サブエージェント run id。実行履歴を開く導線の解決にのみ使う。 */
	subagentRunIdByToolUseId?: ReadonlyMap<string, string>;
	/** run_workflow の tool_use id で引くワークフロー実行の最新状態。 */
	workflowRuns?: ReadonlyMap<string, ChatWorkflowRun>;
	/**
	 * このメッセージ列を記録したサブエージェント実行の状態。終端している場合、応答
	 * (tool_result) が無いまま残った tool_use のエラーと中断を区別して表示する。
	 */
	subagentRunStatus?: SubagentRunStatus;
}

/** テキストバブル。1 メッセージ内の text ブロックを連結して 1 つ生成する。 */
export interface TextRenderBlock {
	kind: "text";
	/** React key 兼 DOM 上の一意キー。 */
	key: string;
	messageId: string;
	role: ChatMessageRole;
	/** 制御行 (attachment 等) を取り除いた表示用テキスト。 */
	text: string;
	controls: MessageControl[];
	images: ImageContentBlock[];
	documents: DocumentContentBlock[];
	uploadedFiles: UploadedFile[];
	createdAt: string;
	/** 送信者のユーザー ID。アシスタントメッセージでは null。 */
	createdById: string | null;
	sendStatus: "sending" | "sent";
}

/** ツールカード。assistant メッセージ内の tool_use ブロック 1 つにつき 1 つ。 */
export interface ToolUseRenderBlock {
	kind: "tool_use";
	key: string;
	messageId: string;
	toolUseId: string;
	name: string;
	/** toolset (browser) のメンバー呼び出しなら "browser"。 */
	toolsetName?: string;
	input: Record<string, unknown>;
	/** tool_result の有無・is_error・HITL 状態から導出する。失敗時は "error"。 */
	status: ToolCallStatus;
	/** 対応する tool_result から抽出した出力。未完了なら undefined。 */
	output?: string;
	/** tool_result にネストした image ブロック (view_image 等)。なければ undefined。 */
	images?: ImageContentBlock[];
	/** 対応するファイル編集ツールが生成した差分。 */
	diff?: FileDiff;
	createdAt: string;
	/**
	 * 実行履歴を開く導線に使う run id。原則 subagentRunIdByToolUseId から解決するが、
	 * まだ一覧取得が済んでいない background 起動直後は backgroundRunId にフォールバックする
	 * (これが無いと起動直後に実行履歴が表示されない)。
	 */
	subagentRunId?: string;
	/** subagentRunId で引いた run の状態。foreground / background を問わず進捗表示に使う。 */
	subagentRun?: ChatSubagentRun;
	/** run_workflow が起動したワークフロー実行。一覧がまだ届いていない間は undefined。 */
	workflowRun?: ChatWorkflowRun;
}

/** 会話の自動要約が挿入された位置を示す区切りブロック。要約本文は折りたたみで表示する。 */
export interface CompactionRenderBlock {
	kind: "compaction";
	key: string;
	messageId: string;
	summaryText: string;
	createdAt: string;
}

/** ユーザーに可視化するシステム通知カード。文言は backend が確定させる。 */
export interface SystemNoticeRenderBlock {
	kind: "system_notice";
	key: string;
	messageId: string;
	noticeKind: NoticeKind;
	text: string;
	createdAt: string;
}

/** モデルプロバイダーの一時障害を知らせ、最新行では再試行操作を提供するカード。 */
export interface ProviderOutageRenderBlock {
	kind: "provider_outage";
	key: string;
	messageId: string;
	text: string;
	createdAt: string;
	/** 連続した障害通知を畳んだ件数 (= 試行回数)。 */
	attemptCount: number;
}

export type RenderBlock =
	| TextRenderBlock
	| ToolUseRenderBlock
	| CompactionRenderBlock
	| SystemNoticeRenderBlock
	| ProviderOutageRenderBlock;

export interface ToolGroup {
	kind: "tool_group";
	key: string;
	tools: ToolUseRenderBlock[];
}
/** groupBlocks の出力は tool_use を必ず tool_group へ畳み込むため、単体の tool_use は含まれない。 */
export type ViewBlock = Exclude<RenderBlock, ToolUseRenderBlock> | ToolGroup;
/** 同じターンのツール呼び出しを 1 つのカードへまとめる。 */
export function groupBlocks(blocks: RenderBlock[]): ViewBlock[] {
	const toolsByTurn = new Map<number, ToolUseRenderBlock[]>(),
		turns = new Map<string, number>();
	let turn = 0;
	for (const block of blocks) {
		if (block.kind === "text") turn++;
		if (block.kind === "tool_use") {
			const tools = toolsByTurn.get(turn) ?? [];
			tools.push(block);
			toolsByTurn.set(turn, tools);
			turns.set(block.key, turn);
		}
	}
	const result: ViewBlock[] = [],
		emitted = new Set<number>();
	for (const block of blocks) {
		if (block.kind !== "tool_use") result.push(block);
		else {
			const current = turns.get(block.key)!;
			if (!emitted.has(current)) {
				emitted.add(current);
				const tools = toolsByTurn.get(current)!;
				result.push({ kind: "tool_group", key: tools[0].key, tools });
			}
		}
	}
	return result;
}

/** 直後のブロックとの関係から、text ブロックが自ターン最後の発言かどうかを返す。 */
export function isLastInTurn(block: TextRenderBlock, next: ViewBlock | undefined): boolean {
	return (
		block.role !== "assistant" ||
		next === undefined ||
		(next.kind === "text" && next.role === "user")
	);
}

/**
 * tool_result の `content` (string か content block 配列) から表示用テキストを取り出す。
 * text ブロック以外は無視し、文字列でも配列でもなければ JSON 文字列化する。
 */
function extractToolResultOutput(content: unknown): string {
	if (typeof content === "string") return content;
	if (Array.isArray(content)) {
		return content
			.map((b) =>
				b && typeof b === "object" && (b as { type?: unknown }).type === "text"
					? String((b as { text?: unknown }).text ?? "")
					: "",
			)
			.join("");
	}
	if (content == null) return "";
	return JSON.stringify(content);
}

/** tool_result の `content` 配列からネストした image ブロックを取り出す。 */
function extractToolResultImages(content: unknown): ImageContentBlock[] {
	return Array.isArray(content) ? content.filter(isImageContent) : [];
}

/**
 * 応答済み tool_result の出力と is_error からツールカードの状態を決める。
 * 停止処理の本文の接頭辞で始まる出力は中断 ("cancelled") として扱う。
 * それ以外は is_error に応じて "error" / "completed"。
 */
export function toolStatusFromResult(result: { output: string; isError: boolean }): ToolCallStatus {
	if (result.isError && result.output.trimStart().startsWith(STOPPED_TOOL_RESULT_PREFIX)) {
		return "cancelled";
	}
	return result.isError ? "error" : "completed";
}

/**
 * 停止を要求されたまま終端していない workflow の子 run があるか。workflow 行は子を待たずに終端する
 * (停止ゲートを見た時点で降りる) ため、行が終端した直後の子は CancelRequested で残る。ここで追うのを
 * やめると、実際には数秒後に終端する子がタスク一覧で「停止中」のまま固まる。
 * 対象を CancelRequested に絞るのは、これが行の終端後に残りうる唯一の非終端状態だからで、
 * 終端処理を取りこぼした実行中の行でポーリングが止まらなくなるのを避けるため。
 */
function hasCancellingWorkflowRun(subagentRuns: ReadonlyMap<string, ChatSubagentRun>): boolean {
	for (const run of subagentRuns.values()) {
		if (run.workflowRunId !== null && run.status === "cancelRequested") return true;
	}
	return false;
}

/**
 * 状態を追い続けるべきサブエージェント実行があるか。ツール自身がまだ実行中のものだけを見る。
 * ツールが終了しているのに run が未終端なのは、終端処理を取りこぼした行 (ワーカーの異常終了など)
 * であり、以後更新されない。これを未終端として扱うとポーリングが止まらなくなる。
 */
export function hasActiveSubagentRun(
	tools: readonly ToolUseRenderBlock[],
	subagentRuns: ReadonlyMap<string, ChatSubagentRun>,
): boolean {
	if (hasCancellingWorkflowRun(subagentRuns)) return true;
	return tools.some((tool) => {
		if (tool.status !== "running") return false;
		// workflow の実行中は workflow 行そのものから導かれている (子 run に対応する tool_use は
		// 親の会話に無い)。追うのをやめると次の波のノードが一覧に現れない。
		if (tool.name === "run_workflow") return true;
		if (tool.subagentRunId === undefined) return false;
		const run = subagentRuns.get(tool.subagentRunId);
		// run 行がまだ読めていない background 起動直後も未終端とみなす。
		return run === undefined || !isTerminalSubagentStatus(run.status);
	});
}

/** run の開始からの経過時間。終了済みなら endedAt まで、実行中は now まで測る。 */
export function formatRunElapsed(startedAt: string, endedAt: string | null, now: number): string {
	const start = Date.parse(startedAt);
	const end = endedAt === null ? now : Date.parse(endedAt);
	if (Number.isNaN(start) || Number.isNaN(end)) return "";
	return formatElapsed(Math.max(0, end - start));
}

/** サブエージェントの進捗 (状態・ターン数・経過時間)。run 未取得なら実行中のみ最低限を返す。 */
export function subagentStatusLabel(tool: ToolUseRenderBlock, now: number): string | null {
	const run = tool.subagentRun;
	if (!run) return tool.status === "running" ? "起動中" : null;
	// ツールが終了しているのに run が未終端なのは、終端処理を取りこぼした行 (ワーカーの異常終了など)。
	// そのまま出すと「実行中」と経過時間が伸び続けるため、結果が確定しているツール側を正とする。
	if (tool.status !== "running" && !isTerminalSubagentStatus(run.status)) return null;
	const parts = [subagentStatusText(run.status)];
	// ターン数は最初の応答を受け取るまで 0 のままなので、その間は添えない。
	if (run.turns > 0) parts.push(`${run.turns}ターン`);
	// 経過を測れない run (終端したのに endedAt が無い) は添えない。タスクペインと同じ扱いにする。
	const elapsed = hasMeasurableElapsed(run)
		? formatRunElapsed(run.startedAt, run.endedAt, now)
		: "";
	if (elapsed) parts.push(elapsed);
	return parts.join(" · ");
}

/** 起動結果から background run id を取り出す。通常のサブエージェント結果は対象外。 */
export function backgroundSubagentRunId(output: string | undefined): string | undefined {
	const match = output?.match(/^\[subagent launched\]\s+run=(srun_[\w-]+)/m);
	return match?.[1];
}

function toolStatusFromWorkflowRun(status: ChatWorkflowRun["status"]): ToolCallStatus {
	switch (status) {
		case "queued":
		case "running":
		case "cancelRequested":
			return "running";
		case "failed":
			return "error";
		case "cancelled":
			return "cancelled";
		case "completed":
			return "completed";
	}
}

function toolStatusFromSubagentRun(status: ChatSubagentRun["status"]): ToolCallStatus {
	switch (status) {
		case "queued":
		case "running":
		case "cancelRequested":
			return "running";
		case "failed":
		case "timedOut":
			return "error";
		case "cancelled":
			return "cancelled";
		case "completed":
			return "completed";
	}
}

/**
 * `ChatMessageDto[]` を描画ブロックの平坦な配列へ射影する。
 *
 * 2 パス構成:
 *   1. 全メッセージの content を走査し tool_use_id → 出力テキストの対応表を作る。
 *   2. メッセージを順に走査し、text ブロックは 1 つのテキストバブルへ連結、
 *      tool_use ブロックはツールカードへ変換する (tool_result ブロックは
 *      パス 1 で消費済みなのでスキップ)。未知の type は無視する (将来の拡張点)。
 */
export type ProjectableMessage = Pick<
	ChatMessageDto,
	"id" | "role" | "content" | "createdAt" | "toolResultMetadata"
> & { createdById?: string | null; stopDetails?: string | null };
export function projectMessages(
	messages: ProjectableMessage[],
	ctx: ProjectionContext,
): RenderBlock[] {
	const resultByToolUseId = new Map<
		string,
		{ output: string; isError: boolean; images: ImageContentBlock[] }
	>();
	const diffByToolUseId = new Map<string, FileDiff>();
	for (const message of messages) {
		if (message.toolResultMetadata?.kind === "fileDiffs") {
			for (const diff of message.toolResultMetadata.diffs) {
				diffByToolUseId.set(diff.toolUseId, diff);
			}
		}
		for (const block of message.content) {
			if (isToolResultContent(block)) {
				resultByToolUseId.set(block.tool_use_id, {
					output: extractToolResultOutput(block.content),
					isError: block.is_error ?? false,
					images: extractToolResultImages(block.content),
				});
			}
		}
	}

	// システムエラー (SystemError ロール) 以降に現れる未応答 tool_use は、システムエラーで
	// 中断されたものとみなす。後続に systemError がある最後の位置を控えておく。
	const lastSystemErrorIndex = messages.findLastIndex((message) => message.role === "systemError");

	const blocks: RenderBlock[] = [];
	for (let i = 0; i < messages.length; i++) {
		const message = messages[i];
		// 外部バックグラウンドタスクのイベントはトランスクリプトに描画しない。
		if (message.role === "monitorEvent" || message.role === "subagentEvent") continue;
		// compaction 行は区切り + 折りたたみ要約として描画する (本文は生の要約テキスト)。
		if (message.role === "compaction") {
			blocks.push({
				kind: "compaction",
				key: `${message.id}#compaction`,
				messageId: message.id,
				summaryText: extractContentText(message.content),
				createdAt: message.createdAt,
			});
			continue;
		}
		// text ブロックを連結し、制御行を分離して 1 つのバブルにする。
		const { text, controls } = parseMessageControls(extractContentText(message.content));
		const images = message.content.filter(isImageContent);
		const documents = message.content.filter(isDocumentContent);
		const uploadedFiles = message.content.filter(isUploadedFileContent).map(fromUploadedFileBlock);
		const noticeKind = parseNoticeKind(message.stopDetails);
		// gateway のアクセス拒否は systemError で届くが、再試行不能な通知として通知カードで見せる。
		if (
			message.role === "systemError" &&
			noticeKind !== null &&
			isProviderAccessNoticeKind(noticeKind)
		) {
			blocks.push({
				kind: "system_notice",
				key: `${message.id}#notice`,
				messageId: message.id,
				noticeKind,
				text,
				createdAt: message.createdAt,
			});
			continue;
		}
		if (message.role === "systemError" && isProviderOutageNotice(message.stopDetails)) {
			// 再試行が同じ障害で失敗した分は直前のカードに畳み、同じ警告帯が積み上がるのを防ぐ。
			// ブロックを生まないメッセージ (monitorEvent 等) は自然に読み飛ばされ、間に表示物が
			// 挟まった場合は別の試行の結果なので畳まない。
			const previous = blocks.at(-1);
			if (previous?.kind === "provider_outage") {
				previous.attemptCount += 1;
				previous.text = text;
				previous.createdAt = message.createdAt;
				continue;
			}
			blocks.push({
				kind: "provider_outage",
				key: `${message.id}#provider-outage`,
				messageId: message.id,
				text,
				createdAt: message.createdAt,
				attemptCount: 1,
			});
			continue;
		}
		// systemNotice は既定で非表示 (停止通知等の内部用途)。stopDetails に明示の通知マーカー
		// (parseNoticeKind) を持つ行だけをユーザー可視カードとして描画する。これが「文言一致でない
		// 明示 opt-in」で、マーカーのない systemNotice は従来どおり抑制する。
		if (message.role === "systemNotice") {
			if (noticeKind !== null) {
				blocks.push({
					kind: "system_notice",
					key: `${message.id}#notice`,
					messageId: message.id,
					noticeKind,
					text,
					createdAt: message.createdAt,
				});
			}
		} else if (
			text.length > 0 ||
			controls.length > 0 ||
			images.length > 0 ||
			documents.length > 0 ||
			uploadedFiles.length > 0
		) {
			blocks.push({
				kind: "text",
				key: `${message.id}#text`,
				messageId: message.id,
				role: message.role,
				text,
				controls,
				images,
				documents,
				uploadedFiles,
				createdAt: message.createdAt,
				createdById: message.createdById ?? null,
				sendStatus:
					message.role === "user" && message.id === ctx.pendingUserMessageId ? "sending" : "sent",
			});
		}

		const rejectedTurn = message.role === "assistant" && isHitlRejectionTurn(message.content);
		// 確認待ちのツールと同じ応答の兄弟は、確認が済むまで実行されない。
		const waitsOnApproval = message.content.some(
			(block) => isToolUseContent(block) && block.id === ctx.waitingToolUseId,
		);
		for (const block of message.content) {
			if (isToolUseContent(block)) {
				const result = resultByToolUseId.get(block.id);
				const isSubagent = block.name === "run_subagent";
				const backgroundRunId = isSubagent ? backgroundSubagentRunId(result?.output) : undefined;
				const subagentRunId = isSubagent
					? (ctx.subagentRunIdByToolUseId?.get(block.id) ?? backgroundRunId)
					: undefined;
				const subagentRun = subagentRunId ? ctx.subagentRuns?.get(subagentRunId) : undefined;
				const workflowRun =
					block.name === "run_workflow" ? ctx.workflowRuns?.get(block.id) : undefined;
				// 投入を断られた呼び出し (is_error) は workflow 行を作らない。承認待ちや応答前も
				// まだ投入されていないので、起動を見たときだけ workflow の進捗へ切り替える。
				const launchedWorkflow =
					block.name === "run_workflow" && result !== undefined && !result.isError;
				let status: ToolCallStatus;
				if (workflowRun !== undefined) {
					// 行があるならそれを正とする。tool_result は中断修復やターン中の停止で is_error や
					// 停止の印に上書きされうるが、workflow はそれでは止まらない (止めるのは停止 API と
					// クレジット枯渇だけ) ため、tool_result を見ると走行中の DAG を終了と誤って描く。
					status = toolStatusFromWorkflowRun(workflowRun.status);
				} else if (launchedWorkflow) {
					// 投入の tool_result は起動した時点で返る。以降の進捗は workflow 行にしか無い。
					// 行が届くまでは実行中として扱う — 投入に成功していれば必ず Queued で存在する。
					status = "running";
				} else if (backgroundRunId !== undefined) {
					// background 起動の tool_result は起動した時点で返るため、以降の進捗は run にしか無い。
					status =
						subagentRun !== undefined ? toolStatusFromSubagentRun(subagentRun.status) : "running";
				} else if (result !== undefined) {
					status = toolStatusFromResult(result);
					// 混在拒否の tool_result はエージェント向けの差し戻し文なので表示しない。
					if (status === "error" && rejectedTurn) status = "skipped";
				} else if (block.id === ctx.waitingToolUseId) {
					status = "waiting_user";
				} else if (subagentRun !== undefined) {
					// foreground は tool_result が届くまで run が唯一の進捗源。
					status = toolStatusFromSubagentRun(subagentRun.status);
				} else if (waitsOnApproval) {
					status = "waiting_user";
				} else if (i < lastSystemErrorIndex) {
					// 応答 (tool_result) が無いまま後続に systemError がある = 中断された tool 呼び出し。
					status = "error";
				} else {
					// 終端した実行では応答が届く見込みがない。失敗・タイムアウトはエラー、
					// それ以外の終端状態は中断として表示する。
					status =
						ctx.subagentRunStatus === "failed" || ctx.subagentRunStatus === "timedOut"
							? "error"
							: ctx.subagentRunStatus === "completed" || ctx.subagentRunStatus === "cancelled"
								? "cancelled"
								: "running";
				}
				blocks.push({
					kind: "tool_use",
					key: `${message.id}#${block.id}`,
					messageId: message.id,
					toolUseId: block.id,
					name: block.name,
					toolsetName: block.toolset_name,
					input: block.input ?? {},
					status,
					output: status === "skipped" ? undefined : result?.output,
					images: result !== undefined && result.images.length > 0 ? result.images : undefined,
					diff: diffByToolUseId.get(block.id),
					createdAt: message.createdAt,
					subagentRunId,
					subagentRun,
					workflowRun,
				});
			}
		}
	}

	return blocks;
}
